import api from "../services/authService";

/**
 * Intake attorney review queue — the sign-up / turn-down call.
 *
 * Mirrors the fee-notice queue's contract: every endpoint returns refusals
 * as `{ error: "..." }`, and a refusal here is usually something the
 * reviewer must act on rather than merely read — turning an intake down
 * without saying why is rejected with `field: "note"` so the surface can
 * put the cursor in the right box instead of showing a toast.
 */

const raise = (error, fallback) => {
  const message =
    error.response?.data?.error || error.response?.data?.message || fallback;
  throw {
    message,
    // Which input caused the refusal, when the backend names one.
    field: error.response?.data?.field,
    status: error.response?.status || 500,
  };
};

export const fetchIntakeReviewListAPI = async (scope = "pending") => {
  try {
    return await api.get(
      `/intake-reviews/?scope=${encodeURIComponent(scope)}`
    );
  } catch (error) {
    return raise(error, "Failed to load the intake review queue");
  }
};

// Opening a card is not a read-only GET as far as the record is
// concerned: it starts the read clock and claims the intake so two
// reviewers cannot decide the same one.
export const fetchIntakeReviewCardAPI = async (id) => {
  try {
    return await api.get(`/intake-reviews/${id}/`);
  } catch (error) {
    return raise(error, "Failed to open that intake");
  }
};

/**
 * One call for every decision and queue move.
 *
 * `secondsOnCard` and `sectionsOpened` are behavioural signal, not
 * telemetry for its own sake: they are how the firm learns which facts
 * actually carry the decision, so they ride along with the decision that
 * they explain rather than going to a separate analytics path.
 */
export const actOnIntakeReviewAPI = async (id, payload) => {
  try {
    return await api.post(`/intake-reviews/${id}/act/`, payload);
  } catch (error) {
    return raise(error, "Failed to record that decision");
  }
};

/**
 * Pull the source queue now, rather than waiting for the poll.
 *
 * The scheduled refresh runs every five minutes, which is fine in
 * steady state and useless to someone standing in front of an empty
 * queue wondering whether it is broken. This returns the sync's own
 * outcome so an upstream failure lands on screen instead of in a log —
 * which is how a broken sync sat unnoticed with 69 intakes waiting.
 */
export const refreshIntakeReviewsAPI = async () => {
  try {
    return await api.post(`/intake-reviews/refresh/`, {});
  } catch (error) {
    return raise(error, "Could not check for new intakes");
  }
};
